import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Safely convert any error-like value (Axios error, string, structured
 * FastAPI 402/422 payload) into a user-friendly string. Never returns an
 * object — passing an object to a React child or Sonner toast crashes the tree.
 */
export function extractApiError(err, fallback = "Something went wrong.") {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  const detail = err.response?.data?.detail ?? err.detail ?? err.message;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // FastAPI 422 validation errors
    return detail.map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d))).join(" · ");
  }
  if (typeof detail === "object") {
    // Structured PLAN_QUOTA_EXCEEDED / other errors — surface something readable
    if (detail.metric_label) {
      return detail.reason === "not_included"
        ? `${detail.metric_label} — not included in your ${detail.current_plan_label || detail.current_plan || "current"} plan.`
        : `${detail.metric_label} — monthly limit reached (${detail.used}/${detail.limit}).`;
    }
    return detail.message || detail.error || fallback;
  }
  return fallback;
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("orixa_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // V8.3 · Workspace-aware header (injected automatically by the client).
  // Backend requires this on every workspace-scoped request; falls back to
  // the caller's default org during the migration window.
  const slug = localStorage.getItem("orixa_workspace_slug");
  if (slug) config.headers["X-Workspace-Slug"] = slug;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      const path = window.location.pathname;
      const isPublic = path === "/" || path.startsWith("/login") || path.startsWith("/signup") || path.startsWith("/pricing") || path.startsWith("/book") || path.startsWith("/auth/callback");
      if (!isPublic) localStorage.removeItem("orixa_token");
    }
    if (err.response?.status === 402) {
      const detail = err.response.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "PLAN_QUOTA_EXCEEDED") {
        try {
          window.dispatchEvent(new CustomEvent("orixa:quota-exceeded", { detail }));
        } catch { /* noop */ }
      }
    }
    return Promise.reject(err);
  }
);
